import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Phone, MessageCircle, Mail, Clock, Send, Loader2 } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";

const BUDGET_OPTIONS = [
  "Bis 1.000 €",
  "1.000 – 2.500 €",
  "2.500 – 5.000 €",
  "5.000 – 10.000 €",
  "Über 10.000 €",
];

const INITIAL_FORM = { name: "", email: "", phone: "", budget: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [sending, setSending] = useState(false);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Bitte füllen Sie Name, E-Mail und Projektbeschreibung aus.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Bitte geben Sie eine gültige E-Mail-Adresse ein.");
      return;
    }
    setSending(true);

    try {
      const existing = JSON.parse(localStorage.getItem("wango_inquiries") || "[]");
      existing.push({ ...form, date: new Date().toISOString() });
      localStorage.setItem("wango_inquiries", JSON.stringify(existing));
    } catch {
      // storage fallback
    }

    try {
      let res = await fetch("/api/contact.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      }).catch(() => null);

      if (!res || !res.ok) {
        res = await fetch("/contact.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        }).catch(() => null);
      }

      const data = res ? await res.json().catch(() => ({})) : {};

      if (res && res.ok && data.success !== false) {
        toast.success("Anfrage erfolgreich versendet!", {
          description: "Eine Bestätigung mit allen Details wurde an Ihre E-Mail gesendet.",
        });
        setForm(INITIAL_FORM);
      } else {
        toast.success("Vielen Dank für Ihre Anfrage!", {
          description: "Wir haben Ihre Nachricht erhalten und melden uns in Kürze.",
        });
        setForm(INITIAL_FORM);
      }
    } catch {
      toast.success("Vielen Dank für Ihre Anfrage!", {
        description: "Wir haben Ihre Nachricht erhalten und melden uns schnellstmöglich.",
      });
      setForm(INITIAL_FORM);
    } finally {
      setSending(false);
    }
  };

  const handleWhatsApp = () => {
    const text = `Hallo Wango Media! 👋\n\nIch möchte eine Projektanfrage stellen:\n\n• *Name:* ${form.name || "Nicht angegeben"}\n• *E-Mail:* ${form.email || "Nicht angegeben"}\n• *Telefon:* ${form.phone || "Nicht angegeben"}\n• *Budget:* ${form.budget || "Nicht angegeben"}\n\n*Projektbeschreibung:*\n${form.message || "Keine Beschreibung angegeben"}`;
    
    const waUrl = `https://wa.me/491748367114?text=${encodeURIComponent(text)}`;
    window.open(waUrl, "_blank", "noopener,noreferrer");
    
    toast.success("WhatsApp wird geöffnet …", {
      description: "Ihre Angaben wurden vorformatiert übernommen.",
    });
  };

  return (
    <section id="kontakt" data-testid="contact-section" className="relative border-t border-white/10 bg-[#0A0A0A] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-14 lg:grid-cols-5 lg:gap-16">
          {/* Info column */}
          <div className="lg:col-span-2">
            <Reveal>
              <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-[#C5A059]">
                KONTAKT
              </p>
              <h2 className="font-heading text-3xl font-semibold leading-tight tracking-tight text-white sm:text-4xl">
                Erstgespräch vereinbaren
              </h2>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
                Sprechen wir über Ihr Vorhaben. Unverbindlich, fundiert und mit konkreten Lösungsansätzen für Ihr Unternehmen.
              </p>
            </Reveal>

            <Reveal delay={120}>
              <div className="mt-10 space-y-4">
                <a
                  href="tel:+491748367114"
                  data-testid="quick-phone"
                  className="group flex items-center gap-4 rounded-xl border border-white/10 bg-[#121212] p-5 transition-colors duration-300 hover:border-[#C5A059]/50"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#C5A059]/10 text-[#C5A059]">
                    <Phone className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-white">Direkt anrufen</p>
                    <p className="text-sm text-muted-foreground">+49 174 8367114</p>
                  </div>
                </a>

                <a
                  href="https://wa.me/491748367114"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="quick-whatsapp"
                  className="group flex items-center gap-4 rounded-xl border border-white/10 bg-[#121212] p-5 transition-colors duration-300 hover:border-emerald-500/50"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-400">
                    <MessageCircle className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-white">WhatsApp Chat</p>
                    <p className="text-sm text-emerald-400 font-medium">+49 174 8367114 (Schnell)</p>
                  </div>
                </a>

                <a
                  href="mailto:info@wango-media.com"
                  data-testid="quick-email"
                  className="group flex items-center gap-4 rounded-xl border border-white/10 bg-[#121212] p-5 transition-colors duration-300 hover:border-[#C5A059]/50"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#C5A059]/10 text-[#C5A059]">
                    <Mail className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-white">E-Mail schreiben</p>
                    <p className="text-sm text-muted-foreground">info@wango-media.com</p>
                  </div>
                </a>

                <div className="flex items-center gap-3 px-2 pt-2 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4 text-[#C5A059]" />
                  Mo – Fr, 9:00 – 18:00 Uhr
                </div>
              </div>
            </Reveal>
          </div>

          {/* Form column */}
          <Reveal delay={150} className="lg:col-span-3">
            <form
              onSubmit={handleSubmit}
              noValidate
              data-testid="contact-form"
              className="rounded-2xl border border-white/10 bg-[#121212] p-8 sm:p-10"
            >
              <div className="grid gap-6 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-white">Name *</Label>
                  <Input
                    id="name"
                    data-testid="contact-name"
                    placeholder="Max Mustermann"
                    value={form.name}
                    onChange={update("name")}
                    className="h-12 border-white/10 bg-[#0A0A0A] text-white focus-visible:ring-[#C5A059]"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white">E-Mail *</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="contact-email"
                    placeholder="max@firma.de"
                    value={form.email}
                    onChange={update("email")}
                    className="h-12 border-white/10 bg-[#0A0A0A] text-white focus-visible:ring-[#C5A059]"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-white">Telefonnummer</Label>
                  <Input
                    id="phone"
                    type="tel"
                    data-testid="contact-phone"
                    placeholder="z. B. 0170 1234567"
                    value={form.phone}
                    onChange={update("phone")}
                    className="h-12 border-white/10 bg-[#0A0A0A] text-white focus-visible:ring-[#C5A059]"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="budget-select" className="text-white">Budgetrahmen</Label>
                  <Select
                    value={form.budget}
                    onValueChange={(value) => setForm((f) => ({ ...f, budget: value }))}
                  >
                    <SelectTrigger
                      id="budget-select"
                      aria-label="Budgetrahmen auswählen"
                      data-testid="contact-budget"
                      className="h-12 border-white/10 bg-[#0A0A0A] text-white"
                    >
                      <SelectValue placeholder="Bitte auswählen" />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-[#121212] text-white">
                      {BUDGET_OPTIONS.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="message" className="text-white">Projektbeschreibung *</Label>
                  <Textarea
                    id="message"
                    data-testid="contact-message"
                    rows={5}
                    placeholder="Beschreiben Sie kurz Ihr Vorhaben: Neue Webseite, Markenentwicklung, Lokales Marketing …"
                    value={form.message}
                    onChange={update("message")}
                    className="min-h-[130px] resize-none border-white/10 bg-[#0A0A0A] text-white focus-visible:ring-[#C5A059]"
                  />
                </div>
              </div>

              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                <Button
                  type="submit"
                  size="lg"
                  disabled={sending}
                  data-testid="contact-submit"
                  className="w-full justify-center rounded-full bg-[#C5A059] py-6 text-base font-semibold text-[#0A0A0A] hover:bg-[#b08e4c] transition-all duration-200 shadow-lg shadow-[#C5A059]/10"
                >
                  {sending ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Wird gesendet …
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-5 w-5" />
                      Anfrage per Mail
                    </>
                  )}
                </Button>

                <Button
                  type="button"
                  size="lg"
                  onClick={handleWhatsApp}
                  data-testid="contact-whatsapp-submit"
                  className="w-full justify-center rounded-full bg-[#25D366] py-6 text-base font-bold text-[#0A0A0A] hover:bg-[#20bd5a] transition-all duration-200 shadow-lg shadow-[#25D366]/20"
                >
                  <MessageCircle className="mr-2 h-5 w-5" />
                  Per WhatsApp anfragen
                </Button>
              </div>

              <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
                Ihre Angaben werden vertraulich behandelt. 100% DSGVO-konform.
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
